import { generatePagination } from "@skv/util";
import classnames from "classnames";
import * as qs from "qs";
import * as React from "react";
import { useLocation, useRouteMatch } from "react-router-dom";

import type { SearchTypes } from "../types";
import { VolunteerSearchLink } from "./";

import {
  OpportunitySearchQueryState,
  OrganisationSearchQueryState,
} from "@skv/state";

type UsePageLocationResult = readonly [
  (
    | (OpportunitySearchQueryState & {
        page: number;
      })
    | (OrganisationSearchQueryState & {
        page: number;
      })
  ),
  string
];

const usePageLocation = (
  searchQuery: OpportunitySearchQueryState | OrganisationSearchQueryState,
  desiredPage: number
): UsePageLocationResult => {
  const location = useLocation();

  const nextPageSearchQuery = React.useMemo(() => {
    return Object.assign({}, searchQuery, { page: desiredPage });
  }, [searchQuery, desiredPage]);

  const nextPageSearchQueryString = React.useMemo(() => {
    return qs.stringify(
      {
        ...qs.parse(location.search, {
          ignoreQueryPrefix: true,
        }),
        page: desiredPage,
      },
      {
        arrayFormat: "repeat",
      }
    );
  }, [location.search, desiredPage]);

  return [nextPageSearchQuery, nextPageSearchQueryString] as const;
};

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  searchQuery: OpportunitySearchQueryState | OrganisationSearchQueryState;
  searchType: SearchTypes;
  isNsw?: boolean;
}

const PaginationListItem: React.FC<{
  isActive: boolean;
  isHiddenMobile: boolean;
  searchQuery: OpportunitySearchQueryState | OrganisationSearchQueryState;
  searchType: SearchTypes;
  page: number;
}> = ({ isActive, isHiddenMobile, searchQuery, searchType, page }) => {
  const [pageQuery, pageQueryString] = usePageLocation(searchQuery, page);
  const { url } = useRouteMatch();

  return (
    <li
      className={classnames(
        "page-item",
        isActive && "active",
        isHiddenMobile && "d-none d-sm-inline-block"
      )}
    >
      {isActive ? (
        <span className="page-link">
          {page} <span className="sr-only">, current page</span>
        </span>
      ) : (
        <VolunteerSearchLink
          className="page-link"
          to={`${url}?${pageQueryString}`}
          searchQuery={
            searchType === "opp"
              ? (pageQuery as OpportunitySearchQueryState)
              : (pageQuery as OrganisationSearchQueryState)
          }
          searchType={searchType}
        >
          {page}
        </VolunteerSearchLink>
      )}
    </li>
  );
};

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  searchQuery,
  searchType,
  isNsw = false,
}) => {
  const match = useRouteMatch();

  const pagesToShow = 5;
  const pagesToDisplay = generatePagination(
    currentPage,
    totalPages,
    pagesToShow
  );

  /* Previous button */
  const prevPage = currentPage > 1 ? currentPage - 1 : 1;

  const [prevSearchQuery, prevSearchQueryString] = usePageLocation(
    searchQuery,
    prevPage
  );

  /* Next button */
  const nextPage = currentPage < totalPages ? currentPage + 1 : totalPages;

  const [nextSearchQuery, nextSearchQueryString] = usePageLocation(
    searchQuery,
    nextPage
  );

  /*
    Actual page numbers
    TODO : This might need a rework
  */

  const activeIdx = pagesToDisplay.indexOf(currentPage);
  const spread = Math.floor(pagesToShow / 2);

  const paginationListItems = pagesToDisplay.map((page, idx) => {
    const isActive = activeIdx === idx;

    const isHiddenMobile = (() => {
      if (activeIdx < spread) {
        return idx > pagesToShow - 1 - spread;
      }

      if (activeIdx > spread) {
        return idx < pagesToShow - (spread + 1);
      }

      return idx === 0 || idx === pagesToShow - 1;
    })();

    return (
      <PaginationListItem
        key={page}
        isActive={isActive}
        isHiddenMobile={isHiddenMobile}
        searchQuery={searchQuery}
        searchType={searchType}
        page={page}
      />
    );
  });

  return (
    <nav aria-label="Page navigation">
      <ul
        className={classnames(
          "pagination flex-wrap justify-content-center",
          isNsw && "pagination--nsw"
        )}
      >
        {currentPage !== 1 ? (
          <li className="page-item">
            <VolunteerSearchLink
              className="page-link"
              to={`${match.url}?${prevSearchQueryString}`}
              searchQuery={
                searchType === "opp"
                  ? (prevSearchQuery as OpportunitySearchQueryState)
                  : (prevSearchQuery as OrganisationSearchQueryState)
              }
              searchType={searchType}
            >
              {isNsw ? (
                <>
                  <i
                    tabIndex={-1}
                    aria-hidden="true"
                    className="material-icons nsw-material-icons mb-1 fs-4"
                  >
                    chevron_left
                  </i>
                  <span className="sr-only">Previous</span>
                </>
              ) : (
                <span aria-label="Previous">Prev</span>
              )}
            </VolunteerSearchLink>
          </li>
        ) : null}

        {paginationListItems}

        {currentPage !== totalPages ? (
          <li className="page-item">
            <VolunteerSearchLink
              className="page-link"
              to={`${match.url}?${nextSearchQueryString}`}
              searchQuery={
                searchType === "opp"
                  ? (nextSearchQuery as OpportunitySearchQueryState)
                  : (nextSearchQuery as OrganisationSearchQueryState)
              }
              searchType={searchType}
            >
              {isNsw ? (
                <>
                  <i
                    tabIndex={-1}
                    aria-hidden="true"
                    className="material-icons nsw-material-icons mb-1 fs-4"
                  >
                    chevron_right
                  </i>
                  <span className="sr-only">Next</span>
                </>
              ) : (
                "Next"
              )}
            </VolunteerSearchLink>
          </li>
        ) : null}
      </ul>
    </nav>
  );
};
